import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Alert
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { colors } from '../styles';
import { getConnectedAccounts } from '../services/api';

const Container = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: '12px',
  backgroundColor: `${colors.primary}05`,
  marginTop: theme.spacing(2)
}));

const AccountRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(1, 1.5),
  borderRadius: '8px',
  '&:hover': {
    backgroundColor: `${colors.primary}10`
  }
}));

const formatAccountLabel = (account) => {
  const mask = account.mask ? ` ••${account.mask}` : '';
  return `${account.name}${mask}`;
};

const AccountMappingsSelector = ({ value = [], onChange }) => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const list = await getConnectedAccounts();
        if (!cancelled) setAccounts(list || []);
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.error || 'Unable to load connected accounts.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const mappedIds = useMemo(
    () => new Set((value || []).map(m => m.plaidAccountId).filter(Boolean)),
    [value]
  );

  const handleToggle = (account) => () => {
    const accountId = account.accountId || account.id;
    if (!accountId) return;

    if (mappedIds.has(accountId)) {
      onChange((value || []).filter(m => m.plaidAccountId !== accountId));
      return;
    }

    onChange([
      ...(value || []),
      {
        plaidAccountId: accountId,
        accountId,
        institutionName: account.institution?.name || account.institutionName || '',
        accountMask: account.mask || ''
      }
    ]);
  };

  return (
    <Container>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
        Route transactions from these accounts
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Transactions from selected accounts will be auto-assigned to this budget.
      </Typography>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={20} />
        </Box>
      )}

      {!loading && error && <Alert severity="error">{error}</Alert>}

      {!loading && !error && accounts.length === 0 && (
        <Alert severity="info">
          Connect a bank account in Settings to start routing transactions automatically.
        </Alert>
      )}

      {!loading && !error && accounts.length > 0 && (
        <Box>
          {accounts.map((account) => {
            const accountId = account.accountId || account.id;
            const institutionName = account.institution?.name || account.institutionName || 'Bank';
            const checked = mappedIds.has(accountId);
            return (
              <AccountRow key={accountId}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={checked}
                      onChange={handleToggle(account)}
                      sx={{ color: colors.primary, '&.Mui-checked': { color: colors.primary } }}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {formatAccountLabel(account)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {institutionName}
                      </Typography>
                    </Box>
                  }
                />
              </AccountRow>
            );
          })}
        </Box>
      )}
    </Container>
  );
};

export default AccountMappingsSelector;
